import {
  FormField,
  FormFieldDescription,
  FormFieldError,
  FormFieldLabel,
  RadioWithLabel,
  spacing,
  Tooltip,
  NewSelect,
} from '@retailer-platform/shared-components'
import React, { type FunctionComponent, useEffect, useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form-7'
import { InputBase } from '@instacart/ids-core'
import _ from 'lodash'
import { useWarehouseContext } from 'utils/contexts/warehouse/WarehouseContext.hooks'
import { useGetRetailerCollectionsOptions } from 'domains/retailer-collections/components/form/utils/useGetRetailerCollectionOptions.hooks'
import { RetailerCollectionsFormSurfaceSelector } from 'domains/retailer-collections/components/form/components/surface-selector/RetailerCollectionsFormSurfaceSelector'
import { RetailerCollectionsFormDatepicker } from 'domains/retailer-collections/components/form/components/datepicker/RetailerCollectionsFormDatepicker'
import {
  type CollectionType,
  type RetailerCollectionFormMode,
  type RetailerCollectionFormValues,
} from 'domains/retailer-collections/components/form/utils/types'
import { useDomainMessages } from 'domains/retailer-collections/utils/domain/intl'
import { type RetailerCollection } from 'domains/retailer-collections/utils/types'
import {
  type InstacartCustomersSharedV1Locale,
  RetailerCollectionContentType,
} from 'domains/retailer-collections/api'
import { useComputedFormData } from 'domains/retailer-collections/components/form/utils/useComputedFormData.hooks'
import { useRetailerCollectionsContext } from 'domains/retailer-collections/utils/retailerCollections.context'

interface RetailerCollectionsFormDetailsProps {
  parentRetailerCollection?: RetailerCollection
  parentRetailerCollectionLevel?: number
  mode: RetailerCollectionFormMode
  collectionType: CollectionType
}

export const RetailerCollectionsFormDetails: FunctionComponent<
  RetailerCollectionsFormDetailsProps
> = ({ parentRetailerCollection, parentRetailerCollectionLevel, mode, collectionType }) => {
  const {
    canBeCollectionsContentType,
    canBeProductsContentType,
    combinedMaxDepth,
    contentTypes: baseContentTypes,
    computedMessages,
    proposedCollectionLevel,
  } = useComputedFormData({
    parentRetailerCollection,
    parentRetailerCollectionLevel,
    formMode: mode,
  })

  const messages = useDomainMessages(
    {
      fieldRequired: 'retailerCollectionsDomain.form.required',
      fieldMin: 'retailerCollectionsDomain.form.min',
      fieldMax: 'retailerCollectionsDomain.form.max',
      parentCollectionLabel: 'retailerCollectionsDomain.form.parentCollection.label',
      parentCollectionDescription: 'retailerCollectionsDomain.form.parentCollection.description',
      parentCollectionDepthError:
        'retailerCollectionsDomain.form.parentCollection.parentCollection.depthError',
      parentCollectionRequired: 'retailerCollectionsDomain.form.parentCollection.required',

      surfacesLabel: 'retailerCollectionsDomain.form.surfaces.label',
      surfacesDescription: 'retailerCollectionsDomain.form.surfaces.description',
      surfacesError: 'retailerCollectionsDomain.form.surfaces.error',
      nameLabel: 'retailerCollectionsDomain.form.name.label',
      slugLabel: 'retailerCollectionsDomain.form.slug.label',
      slugRequired: 'retailerCollectionsDomain.form.slug.required',
      slugDescription: 'retailerCollectionsDomain.form.slug.description',
      contentTypeLabel: 'retailerCollectionsDomain.form.contentType.label',
      contentTypeCollections: 'retailerCollectionsDomain.form.contentType.value.collections',
      contentTypeProducts: 'retailerCollectionsDomain.form.contentType.value.products',
      effectiveDatesLabel: 'retailerCollectionsDomain.form.effectiveDates.label',
      effectiveDatesDescription: 'retailerCollectionsDomain.form.effectiveDates.description',
      effectiveDatesError: 'retailerCollectionsDomain.form.effectiveDates.error',
      positionLabel: 'retailerCollectionsDomain.form.position.label',
      positionDescription: 'retailerCollectionsDomain.form.position.description',
    },
    {
      maxDepth: combinedMaxDepth,
      minValue: 0,
      maxValue: Number.MAX_SAFE_INTEGER,
      collectionLevel: proposedCollectionLevel + 1,
      numCollections: 1,
    }
  )

  const { storeConfigurations } = useRetailerCollectionsContext()
  const instacartCollectionEdit = collectionType === 'instacart' && mode === 'edit'

  const { warehouse } = useWarehouseContext()

  const { options, loading } = useGetRetailerCollectionsOptions({
    // Subtract one as the max level for the parent should be 1 less than the max.
    maxLevel: combinedMaxDepth - 1,
  })

  // when contentType is "PRODUCTS" we cannot add as a top-level collection
  if (mode === 'edit' || mode === 'duplicate') {
    // Remove "No Parent Collection" value
    _.remove(options, option => !option.value)
  }

  const {
    control,
    register,
    formState: { errors },
    watch,
    setValue,
    getValues,
  } = useFormContext<RetailerCollectionFormValues>()

  // Disable content types changes when in edit mode
  const contentTypes = useMemo(
    () =>
      baseContentTypes.map(v => ({
        ...v,
        disabled: mode === 'edit' || mode === 'duplicate' || v.disabled,
      })),
    [baseContentTypes, mode]
  )

  const defaultLocale =
    warehouse.defaultLocale.localeCode.toUpperCase() as InstacartCustomersSharedV1Locale
  const translations = watch('translations')
  const name = watch('name')

  // Keep the default translation and name in sync.
  useEffect(() => {
    const translations = getValues('translations')
    const defaultTranslation = translations.findIndex(
      ({ localeCode }) => localeCode === defaultLocale
    )

    // Bail if not found
    if (defaultTranslation < 0) return

    setValue(`translations.${defaultTranslation}.name`, name)
  }, [name, getValues, setValue, defaultLocale])

  // Automatically fix contentType
  // if parentRetailerCollection is unset or if a max level collection is selected
  const currentContentType = watch('contentType')
  useEffect(() => {
    // If in edit mode do not change from the initial contentType
    // This is somehow needed. Otherwise when inputs are disabled, at some point the value is lost?
    // Is this a bug on our side or RHF (or intended behavior, even?)?
    // I suspect it has something to do with disabled fields as mentioned here
    // https://react-hook-form.com/api/useform/getvalues#rules
    if (mode === 'edit' || mode === 'duplicate') {
      setValue('contentType', currentContentType)
      return
    }

    if (!canBeProductsContentType) {
      setValue('contentType', RetailerCollectionContentType.Collections)
    } else if (!canBeCollectionsContentType) {
      setValue('contentType', RetailerCollectionContentType.Products)
    }
  }, [
    parentRetailerCollection,
    currentContentType,
    setValue,
    getValues,
    mode,
    canBeCollectionsContentType,
    canBeProductsContentType,
  ])

  // This will hold the status value, eventually should become an actual control?
  register('status')

  return (
    <React.Fragment>
      <FormField>
        <FormFieldLabel>{messages.parentCollectionLabel}</FormFieldLabel>

        <FormFieldDescription>{messages.parentCollectionDescription}</FormFieldDescription>

        <Controller
          name="parentId"
          control={control}
          rules={{
            validate: () => {
              if (parentRetailerCollectionLevel >= 2) return messages.parentCollectionDepthError

              return true
            },
          }}
          render={({ field: { ref: _ref, ...fieldProps } }) => (
            <NewSelect
              // disable choosing a different parent for now while duplicating due to complication introduced
              // by duplicating IC collections . Can remove this restriction later when we add
              // functionality to get number of levels the IC collection has from getInstacartCollectionQuery
              isDisabled={mode === 'duplicate' || instacartCollectionEdit}
              options={options || []}
              {...fieldProps}
              isLoading={loading}
            />
          )}
        />

        {errors.parentId && <FormFieldError>{messages.parentCollectionRequired}</FormFieldError>}
      </FormField>

      {/* Dont allow changing store configurations of "Subjects" (An IC collection that holds products) */}
      {watch('parentId') == null && (
        <FormField>
          <FormFieldLabel>{messages.surfacesLabel}</FormFieldLabel>

          <FormFieldDescription>{messages.surfacesDescription}</FormFieldDescription>

          <Controller
            name="storeConfigurations"
            control={control}
            render={({ field: { ref: _ref, ...fieldProps } }) => (
              <RetailerCollectionsFormSurfaceSelector
                options={storeConfigurations}
                parentOptions={parentRetailerCollection?.storeConfigurations}
                collectionType={collectionType}
                {...fieldProps}
              />
            )}
          />

          {errors.surfaces && <FormFieldError>{errors.surfaces?.message}</FormFieldError>}
        </FormField>
      )}

      <FormField>
        <FormFieldLabel>{messages.nameLabel}</FormFieldLabel>
        <InputBase
          {...register(`name`, { required: messages.fieldRequired })}
          id="name"
          disabled={instacartCollectionEdit}
        />
        {errors.name && <FormFieldError>{errors.name?.message}</FormFieldError>}
      </FormField>

      {translations.map(({ localeCode }, i) => {
        // The default locale name is not shown in this list, and `name` is the source of truth
        if (localeCode === defaultLocale) return null

        return (
          <FormField key={i}>
            <FormFieldLabel optional>
              {localeCode} {messages.nameLabel}
            </FormFieldLabel>

            <input {...register(`translations.${i}.localeCode`)} type="hidden" value={localeCode} />

            <InputBase
              {...register(`translations.${i}.name`)}
              id={`locale-name-${localeCode}`}
              disabled={instacartCollectionEdit}
            />
          </FormField>
        )
      })}

      <FormField>
        <FormFieldLabel>{messages.slugLabel}</FormFieldLabel>
        <FormFieldDescription>{messages.slugDescription}</FormFieldDescription>

        <InputBase
          {...register(`slug`, { required: messages.slugRequired })}
          id={`slug`}
          disabled={mode === 'edit'}
        />
        {errors.slug && <FormFieldError>{errors.slug?.message}</FormFieldError>}
      </FormField>

      <FormField>
        <FormFieldLabel>{messages.contentTypeLabel}</FormFieldLabel>

        <FormFieldDescription>{computedMessages.contentTypeMessage}</FormFieldDescription>

        <div
          css={{
            display: 'flex',
            flexDirection: 'row',
            gap: spacing.X12,
            paddingTop: spacing.X12,
          }}
        >
          {contentTypes.map(contentType => {
            const contentTypeId = `content-type-${contentType.value}`

            return (
              <Tooltip
                key={contentTypeId}
                placement="bottom"
                target={
                  <RadioWithLabel
                    key={contentTypeId}
                    {...register(`contentType`)}
                    id={contentTypeId}
                    label={contentType.label}
                    value={contentType.value}
                    disabled={contentType.disabled || instacartCollectionEdit}
                    checked={currentContentType.toLowerCase() === contentType.value.toLowerCase()}
                  />
                }
              >
                {contentType.disabled
                  ? computedMessages.contentTypeDisabledTooltipMessage
                  : contentType.tooltipMessage}
              </Tooltip>
            )
          })}
        </div>
      </FormField>

      <FormField>
        <FormFieldLabel optional>{messages.effectiveDatesLabel}</FormFieldLabel>
        <FormFieldDescription>{messages.effectiveDatesDescription}</FormFieldDescription>

        <Controller
          name="effectiveDates"
          control={control}
          render={({ field: { ref: _ref, ...fieldProps } }) => (
            <RetailerCollectionsFormDatepicker {...fieldProps} disabled={instacartCollectionEdit} />
          )}
          rules={{
            validate: ({ startDate, endDate }) => {
              if (startDate && !endDate) return messages.effectiveDatesError

              return true
            },
          }}
        />

        {errors.effectiveDates && <FormFieldError>{errors.effectiveDates?.message}</FormFieldError>}
      </FormField>

      <FormField>
        <FormFieldLabel>{messages.positionLabel}</FormFieldLabel>
        <FormFieldDescription>{messages.positionDescription}</FormFieldDescription>

        <InputBase
          {...register(`position`, {
            min: { value: 0, message: messages.fieldMin },
            max: { value: Number.MAX_SAFE_INTEGER, message: messages.fieldMax },
            valueAsNumber: true,
          })}
          id={`position`}
          type={`number`}
          disabled={instacartCollectionEdit}
        />
        {errors.position && <FormFieldError>{errors.position?.message}</FormFieldError>}
      </FormField>
    </React.Fragment>
  )
}
